import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { LANGUAGE } from '@citylytics/domain/languages/constants';
import { setupCoreApiClient } from '@citylytics/web-api-client/core-api-client/service';
import { readCurrentLanguage, setupWebI18n } from '@citylytics/web-i18n/i18n/service';
import { setupSupertokens } from '@citylytics/web-session/supertokens/service';
import './index.css';
import { CONFIG } from './config';
import type { AppTranslations } from './lib/i18n/i18n.types';
import da from './lib/i18n/locales/da.json';
import en from './lib/i18n/locales/en.json';
import sv from './lib/i18n/locales/sv.json';
import { getQueryClient } from './lib/query-client/query-client.singleton';
import { getRouter } from './lib/router/router.singleton';
import { leaveLostSession } from './lib/session/session.singleton';

// client-web is what a municipality's own users see, so it speaks more than admin-web.
setupWebI18n({
  languages: [LANGUAGE.EN, LANGUAGE.SV, LANGUAGE.DA],
  translations: { en, sv, da } satisfies Record<string, AppTranslations>,
  // Per app, so choosing a language in one app doesn't change another on the same origin.
  localStorageKey: 'citylytics.client-web.lng',
});
setupSupertokens({ apiDomain: CONFIG.CORE_API_URL });
setupCoreApiClient({
  baseUrl: CONFIG.CORE_API_URL,
  readLanguage: readCurrentLanguage,
  onSessionLost: () => leaveLostSession(getRouter()),
});

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error('index.html has no #root element to render into');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={getQueryClient()}>
      <RouterProvider router={getRouter()} />
    </QueryClientProvider>
  </StrictMode>,
);
